// [BACKDROP]: a lower-left/upper-right world-coordinate extent for an image
// drawn behind the network — no rotation, not a general affine. This file
// only reads/writes the CALIBRATION; the image bytes themselves live in
// `StoredProject.backgroundImageBlob` (storage.ts), IndexedDB-only —
// exporting to `.inp` carries the calibration over, never the image.

import type { Point } from "./types.ts";

const SECTION = "BACKDROP";

export interface BackdropCalibration {
  lowerLeft: Point;
  upperRight: Point;
}

/** `null` when there's no `[BACKDROP]` section, or its own DIMENSIONS row is missing/unparseable. */
export function readBackdropCalibration(
  raw: ReadonlyMap<string, string[][]>
): BackdropCalibration | null {
  const rows = raw.get(SECTION) ?? [];
  const dims = rows.find((r) => r[0]?.toUpperCase() === "DIMENSIONS");
  if (!dims) return null;
  const [llx, lly, urx, ury] = [dims[1], dims[2], dims[3], dims[4]].map(Number);
  if (![llx, lly, urx, ury].every(Number.isFinite)) return null;
  return { lowerLeft: { x: llx, y: lly }, upperRight: { x: urx, y: ury } };
}

/** Adds a placeholder FILE row if this project never had one — this app has no real on-disk file path for it to name. */
export function writeBackdropCalibration(
  raw: ReadonlyMap<string, string[][]>,
  calibration: BackdropCalibration
): Map<string, string[][]> {
  const rows = (raw.get(SECTION) ?? []).filter((r) => r[0]?.toUpperCase() !== "DIMENSIONS");
  // Unquoted — `rawSections` values are always the tokenizer's own
  // UNQUOTED form (same as every value `parseInpSections` ever produces);
  // `serializeInp`'s own `formatToken` is what decides whether a value
  // needs quoting on export, not this call site.
  if (!rows.some((r) => r[0]?.toUpperCase() === "FILE")) rows.unshift(["FILE", "background"]);
  rows.push([
    "DIMENSIONS",
    String(calibration.lowerLeft.x),
    String(calibration.lowerLeft.y),
    String(calibration.upperRight.x),
    String(calibration.upperRight.y),
  ]);
  const next = new Map(raw);
  next.set(SECTION, rows);
  return next;
}

export function clearBackdropCalibration(
  raw: ReadonlyMap<string, string[][]>
): Map<string, string[][]> {
  const next = new Map(raw);
  next.delete(SECTION);
  return next;
}
